import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import ProviderManager from '../../../provider/providerManager';
import {showAppLoader, hideAppLoader} from '../../Shared/Loader/LoaderSlice';
import {FLOW_STEPS, RECIPIENT_TYPES, messageData, BENEFIT_TABS} from './Conatant';



const mapProductQuantitiesToAllocations = (quantities = {}, productQuantities = {}) =>
  Object.keys(quantities).reduce((acc, key) => {
    acc[key] = Number(productQuantities[key]) || 0;
    return acc;
  }, {});



const mapFamilyMemberToState = (member, quantities = {}) => ({
  firstName: member.firstName,
  lastName: member.lastName,
  phoneNumber: member.phoneNumber,
  allocations: mapProductQuantitiesToAllocations(quantities, member.productQuantities),
});

export const GetOrganizationFamilyData = createAsyncThunk(
  'organizationEmployee/GetOrganizationFamilyData',
  async (data, thunkAPI) => {
    try {
      thunkAPI.dispatch(showAppLoader());
      const result = await ProviderManager.getProviderData('GetOrganizationFamilyData', []);
      thunkAPI.dispatch(hideAppLoader());
      return result.Body;
    } catch (error) {
      thunkAPI.dispatch(hideAppLoader());
      throw error;
    }
  },
);


export const AssignBenefitsToOrgFamilyMember = createAsyncThunk(
  'organizationEmployee/AssignBenefitsToOrgFamilyMember',
  async (data, thunkAPI) => {
    try {
      thunkAPI.dispatch(showAppLoader());
      const result = await ProviderManager.getProviderData('AssignBenefitsToOrgFamilyMember', data);
      thunkAPI.dispatch(hideAppLoader());
      var message = result.Body.isSuccess ? messageData.BenefitsSentSuccessfully :{...messageData.BenefitsSentError, closePopup: 'goToBenefits'};
      thunkAPI.dispatch(setMessageData(message));
      return result.Body;
    } catch (error) {
      thunkAPI.dispatch(hideAppLoader());
      throw error;
    }
  },
);

export const SendBenefitsSmsToOrgFamilyMember = createAsyncThunk(
  'organizationEmployee/SendBenefitsSmsToOrgFamilyMember',
  async (phone, thunkAPI) => {
    try {
      thunkAPI.dispatch(showAppLoader());
      const result = await ProviderManager.getProviderData('SendBenefitsSmsToOrgFamilyMember', [phone]);
      if(result.Body.isSuccess) {
        thunkAPI.dispatch(setNotifiedMembers(phone));
      }
      thunkAPI.dispatch(hideAppLoader());
      var message = result.Body.isSuccess ? messageData.notificationSentSuccessfully : messageData.BenefitsSentError;
      thunkAPI.dispatch(setMessageData(message));
      return result.Body;
    }
    catch (error) {
      thunkAPI.dispatch(hideAppLoader());
      throw error;
    }
  },
);

const initialState = {
  epiContent: '',
  orgName: '',
  productsDescription: {},
  familyMembers: [],
  draftMember: {
    firstName: '',
    lastName: '',
    phoneNumber: '',
    allocations: {},
  },
  selectedBenefits: [],
  recipientType: RECIPIENT_TYPES.FAMILY,
  currentStep: FLOW_STEPS.BENEFITS,
  isSettingsLoaded: false,
  isSubmitSuccess: false,
  showPopup: false,
  notifiedMembers: [],
  activeTab: BENEFIT_TABS.ALL,
};

const OrganizationFamilyMemberSlice = createSlice({
  name: 'organizationEmployee',
  initialState: initialState,
  reducers: {
    saveToHistory: (state, action) => {
      return state;
    },
    pushToHistory: (state, action) => {
      return state;
    },
    clearHistory: (state, action) => {
      return initialState;
    },
    backHistory: (state, action) => {
      return state;
    },
    resetState: () => initialState,

    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
      state.showPopup = false;
      if(action.payload === FLOW_STEPS.BENEFITS) {
        state.activeTab = BENEFIT_TABS.ALL;
      }
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    setMessageData: (state, action) => {
      state.messageData = action.payload;
      state.showPopup = true;
    },
    setNotifiedMembers: (state, action) => {
      state.notifiedMembers = [...state.notifiedMembers, action.payload];
    },
    toggleSelectedBenefit: (state, action) => {
      const productKey = action.payload;
      const index = state.selectedBenefits.indexOf(productKey);
      if (index >= 0) {
        state.selectedBenefits.splice(index, 1);
        if (state.draftMember?.allocations) {
          state.draftMember.allocations[productKey] = 0;
        }
      } else {
        state.selectedBenefits.push(productKey);
      }
    },
    setSelectedBenefits: (state, action) => {
      state.selectedBenefits = action.payload || [];
    },
    setRecipientType: (state, action) => {
      state.recipientType = action.payload;
    },
    resetDraftMember: (state) => {
      state.draftMember = {
        firstName: '',
        lastName: '',
        phoneNumber: '',
        allocations: {},
      };
    },
    updateDraftMemberField: (state, action) => {
      const {field, value} = action.payload;
      if (!state.draftMember || !field) {
        return;
      }
      state.draftMember[field] = value;
    },
    updateDraftAllocation: (state, action) => {
      const {productKey, value} = action.payload;
      const product = state.productsDescription?.[productKey];
      if (!state.draftMember || !product) {
        return;
      }
      if (!state.draftMember.allocations) {
        state.draftMember.allocations = {};
      }
      const maxAllowed = Math.max(0, Number(product.remaining) || 0);
      state.draftMember.allocations[productKey] = Math.max(0, Math.min(Number(value) || 0, maxAllowed));
    },

    setShowPopup: (state, action) => {
      state.showPopup = action.payload;
    },

    addFamilyMember: (state) => {
      state.familyMembers.push(createEmptyMember(state.productsDescription));
    },
    removeFamilyMember: (state, action) => {
      if (state.familyMembers.length <= 1) {
        return;
      }
      const phone = action.payload;
      state.familyMembers = state.familyMembers.filter(
        (member) => member.phoneNumber !== phone,
      );
    },
    updateMemberField: (state, action) => {
      const {phoneNumber, field, value} = action.payload;
      const member = state.familyMembers.find(
        (item) => item.phoneNumber=== phoneNumber,
      );
      if (member && field) {
        member[field] = value;
      }
    },
    updateMemberAllocation: (state, action) => {
      const {phoneNumber, productKey, value} = action.payload;
      const member = state.familyMembers.find((item) => item.phoneNumber === phoneNumber);
      if (!member) {
        return;
      }
      const total = state.productsDescription[productKey].quantity;
      const usedByOthers = state.familyMembers.reduce((sum, item) => {
        if (item.phoneNumber === phoneNumber) {
          return sum;
        }
        return sum + (Number(item.allocations[productKey]) || 0);
      }, 0);
      const maxAllowed = Math.max(0, total - usedByOthers);
      member.allocations[productKey] = Math.max(0, Math.min(Number(value) || 0, maxAllowed));
    },
  },

  extraReducers: {
    [GetOrganizationFamilyData.fulfilled]: (state, action) => {
      const description = action.payload?.productsDescription || {};
      const familyMembers = action.payload?.familyMembers || [];
      state.productsDescription = description;
      state.isSettingsLoaded = true;
      state.familyMembers = familyMembers.map((member) => mapFamilyMemberToState(member, description));
      state.orgName = action.payload?.organizationName || '';
    },
    [GetOrganizationFamilyData.pending]: (state, action) => {
      state.isSettingsLoaded = false;
    },
    [GetOrganizationFamilyData.rejected]: (state, action) => {
      state.isSettingsLoaded = true;
      state.productsDescription = {};
    },
    [AssignBenefitsToOrgFamilyMember.rejected]: (state, action) => {
      state.messageData = messageData.BenefitsSentError;

    },
  },
});

export const {
  saveToHistory,
  pushToHistory,
  clearHistory,
  backHistory,
  resetState,
  setCurrentStep,
  toggleSelectedBenefit,
  setSelectedBenefits,
  setRecipientType,
  resetDraftMember,
  updateDraftMemberField,
  updateDraftAllocation,
  addFamilyMember,
  removeFamilyMember,
  updateMemberField,
  updateMemberAllocation,
  setMessageData,
  setShowPopup,
  setNotifiedMembers,
  setActiveTab,
} = OrganizationFamilyMemberSlice.actions;

export default OrganizationFamilyMemberSlice.reducer;
